/**
 * Iter269.20d — TrustSelfAttestStrip
 *
 * Horizontal stat strip that proves QorTrace's own operational posture
 * with live numbers pulled from /api/public/status:
 *   • 30-day uptime % (from the running synthetic monitor)
 *   • TLS cert days remaining
 *   • DB latency
 *   • Subsystem health (operational/degraded count)
 *
 * Most SaaS Trust Centers are static screenshots. This component
 * self-attests: numbers update in real time from our own running
 * monitors. The CISO landing on /trust can refresh and watch the
 * "checked X seconds ago" timestamp tick.
 */
import React, { useEffect, useState } from "react";
import axios from "axios";
import { Activity, ShieldCheck, Clock, Zap, Lock, ExternalLink, Loader2 } from "lucide-react";

const API = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "");

function formatRelative(iso) {
  if (!iso) return "—";
  const then = new Date(iso);
  const diffSec = Math.max(0, Math.floor((Date.now() - then.getTime()) / 1000));
  if (diffSec < 60) return `${diffSec}s ago`;
  if (diffSec < 3600) return `${Math.floor(diffSec / 60)}m ago`;
  if (diffSec < 86400) return `${Math.floor(diffSec / 3600)}h ago`;
  return `${Math.floor(diffSec / 86400)}d ago`;
}

export default function TrustSelfAttestStrip() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    axios.get(`${API}/api/public/status`)
      .then((r) => { if (alive) setData(r.data); })
      .catch(() => { if (alive) setData(null); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, []);

  if (loading) {
    return (
      <div
        className="rounded-xl border p-6 flex items-center gap-3"
        style={{ borderColor: "var(--qt-border-soft)", background: "rgba(4,9,20,0.5)" }}
        data-testid="trust-self-attest-loading"
      >
        <Loader2 size={14} className="animate-spin" style={{ color: "var(--qt-text-3)" }} />
        <span className="text-xs qt-mono" style={{ color: "var(--qt-text-3)" }}>
          Probing QorTrace's own posture…
        </span>
      </div>
    );
  }
  if (!data) return null;

  const uptime  = data.uptime_pct_30d;
  const latency = data.api_latency?.db_ping_ms;
  const subsystems = data.subsystems || [];
  const subOpCount  = subsystems.filter((s) => s.status === "operational").length;
  const subTotal    = subsystems.length;
  const primaryCert = (data.cert_dns || []).find((c) => c.host === "qortrace.com")
                   || (data.cert_dns || [])[0];
  const certDays  = primaryCert?.ssl?.days_remaining;

  const stats = [
    {
      key: "uptime",
      label: "30-day uptime",
      value: uptime != null ? `${Number(uptime).toFixed(2)}%` : "—",
      tone: uptime >= 99.9 ? "green" : uptime >= 99.0 ? "amber" : "red",
      icon: Activity,
      detail: "from our own 5-min synthetic monitor",
    },
    {
      key: "tls",
      label: "TLS cert",
      value: certDays != null ? `${certDays} days` : "—",
      tone: certDays >= 30 ? "green" : certDays >= 14 ? "amber" : "red",
      icon: Lock,
      detail: primaryCert?.host
        ? `${primaryCert.host} · auto-renewed by Cloudflare`
        : "auto-renewed by Cloudflare",
    },
    {
      key: "latency",
      label: "DB latency",
      value: latency != null ? `${Math.round(latency)}ms` : "—",
      tone: latency <= 50 ? "green" : latency <= 200 ? "amber" : "red",
      icon: Zap,
      detail: "round-trip to MongoDB Atlas",
    },
    {
      key: "subsystems",
      label: "Subsystems",
      value: subTotal > 0 ? `${subOpCount}/${subTotal} green` : "—",
      tone: subOpCount === subTotal ? "green" : subOpCount >= subTotal * 0.9 ? "amber" : "red",
      icon: ShieldCheck,
      detail: "API, scanner, audit engine, ingest…",
    },
  ];

  const toneColors = { green: "#1FDD83", amber: "#FFB000", red: "#FF6E6E" };

  return (
    <div className="my-8" data-testid="trust-self-attest-strip">
      <div
        className="flex items-center justify-between mb-3 flex-wrap gap-2"
        style={{ color: "var(--qt-text-3)" }}
      >
        <div className="qt-mono text-[10px]" style={{ letterSpacing: "0.18em", textTransform: "uppercase" }}>
          QorTrace's own posture · live from /api/public/status
        </div>
        <div className="qt-mono text-[10px] flex items-center gap-2">
          <Clock size={10} /> probed {formatRelative(data.checked_at)}
          <a
            href="/status"
            data-testid="trust-self-attest-status-link"
            className="inline-flex items-center gap-1 hover:opacity-80"
            style={{ color: "var(--qt-cyan)", letterSpacing: "0.08em" }}
          >
            view full status <ExternalLink size={10} />
          </a>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="trust-self-attest-grid">
        {stats.map(({ key, label, value, tone, icon: Ic, detail }) => (
          <div
            key={key}
            data-testid={`trust-self-attest-${key}`}
            className="qt-card flex flex-col"
            style={{
              padding: 16,
              background: "rgba(4,9,20,0.5)",
              borderColor: "var(--qt-border-soft)",
              minHeight: 110,
            }}
          >
            <div className="flex items-center gap-2 mb-2">
              <Ic size={14} style={{ color: toneColors[tone] }} />
              <span
                className="qt-mono text-[10px]"
                style={{ color: "var(--qt-text-3)", letterSpacing: "0.14em", textTransform: "uppercase" }}
              >
                {label}
              </span>
            </div>
            <div
              className="text-2xl font-semibold leading-none"
              style={{
                color: toneColors[tone],
                fontFamily: "Chivo, sans-serif",
                letterSpacing: "-0.02em",
              }}
            >
              {value}
            </div>
            <div className="text-[11px] mt-2" style={{ color: "var(--qt-text-3)", lineHeight: 1.4 }}>
              {detail}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
